import { StrictMode } from "react";
import { I18nextProvider } from "react-i18next";

import { LazyHotReloadIndicator, LazyReactAxe } from "@shared/components";
import { Route, Routes } from "@shared/react-router-dom";
import { wildcardPath } from "@shared/utils";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
/* eslint-disable @fullscript/cross-reference */
import { ErrorBoundary } from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { ForgotPasswordPage } from "@unauthenticated/signupSignin/scenes/ForgotPasswordPage/ForgotPasswordPage";
/* eslint-enable @fullscript/cross-reference */
import { i18n } from "@unauthenticated/wellevateAuthorize/patients/locales/i18n";

import { Providers } from "../../components";
import { WellevateAuthorizeFlow } from "../components";

interface Props {
  currentCountry: string;
  patientAttributes: PatientAttributes;
  emailRegex: string;
  treatmentPlanTerm: string;
  csuid: string;
  roleId: string;
  storeId: string;
  additionalAttributes?: any;
  ssoErrors?: string;
  isNewPatient: boolean;
  error?: string;
  isEmailCapture?: boolean;
  analyticsAnonymousId: string;
}

const WellevateAuthorizePage = ({
  currentCountry,
  patientAttributes,
  emailRegex,
  treatmentPlanTerm,
  csuid,
  roleId,
  storeId,
  additionalAttributes,
  ssoErrors,
  isNewPatient,
  error,
  isEmailCapture,
  analyticsAnonymousId,
}: Props) => {
  const pageVersion = "react_with_sso";
  const { storeSlug, treatmentPlanSlug, uuid } = patientAttributes;
  const identitySlug = uuid || treatmentPlanSlug;

  return (
    <StrictMode>
      <Providers
        emailRegex={emailRegex}
        country={currentCountry}
        treatmentPlanTerm={treatmentPlanTerm}
        analyticsAnonymousId={analyticsAnonymousId}
      >
        <I18nextProvider i18n={i18n}>
          <ErrorBoundary {...{ flippers: [], csuid, roleId, storeId, pageVersion }}>
            <Routes>
              <Route
                path={authRoutes.wellevate_patients_authorize}
                element={
                  <WellevateAuthorizeFlow
                    patientAttributes={patientAttributes}
                    additionalAttributes={additionalAttributes}
                    ssoErrors={ssoErrors}
                    isNewPatient={isNewPatient}
                    error={error}
                    pageVersion={pageVersion}
                    isEmailCapture={isEmailCapture}
                  />
                }
              />
              <Route
                path={wildcardPath(authRoutes.welcome_forgot_password(storeSlug, identitySlug))}
                element={<ForgotPasswordPage />}
              />
            </Routes>
          </ErrorBoundary>
        </I18nextProvider>
        <LazyHotReloadIndicator />
        <LazyReactAxe />
      </Providers>
    </StrictMode>
  );
};

export { WellevateAuthorizePage };
